import type { CadenceFrequency } from '@/types';
import { WeekDay } from '@/types';

/**
 * Single source of truth for the merged Scan Schedule summary sentence.
 *
 * Pure (no React, no hooks) so it is trivially unit-testable (FIDELITY I1).
 * Consumed identically by the collapsed card (§A5) and the wizard live
 * summary band (§C4). Templates are transcribed verbatim from the approved
 * mockup `<script>` (approved-mockup.html:348-413) and FIDELITY-CHECKLIST §E.
 *
 * Copy fidelity: en-dash `–` (U+2013) in the time window; middot `·` (U+00B7)
 * in the quarterly month list; literal ellipsis `…` in the pending sentence.
 */

export type { CadenceFrequency };

export interface ScanScheduleSummaryState {
  frequency: CadenceFrequency;
  days: WeekDay[]; // 0=Sun..6=Sat
  start: string; // "HH:MM"
  end: string; // "HH:MM"
  /**
   * Day-of-month the monthly/quarterly period begins on (1-31). `31` is the
   * "Last day" sentinel — the backend clamps it to each month's true last day,
   * so it renders as "the last day". Optional: when undefined (caller didn't
   * pass it) the day clause is omitted and the sentence reads as it did before
   * the day-of-month picker existed. Ignored for daily/weekly/biweekly.
   */
  dayOfMonth?: number;
  /**
   * True when the window spans the whole day (no time bound). The sentence then
   * reads "… all day." instead of "… between {start}–{end} UTC.".
   */
  allDay?: boolean;
}

/** A styled run of text. The renderer maps these flags to chariot tokens. */
export type SentenceBodyPart =
  | { text: string } // plain
  | { text: string; accent: true } // frequency phrase (brand-primary, bold)
  | { text: string; bold: true } // day list / "first {weekday}"
  | { text: string; mono: true }; // time window "HH:MM–HH:MM UTC"

const DOW_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// freqLead — approved-mockup.html:348-350
export const freqLead = (f: CadenceFrequency): string =>
  ({
    daily: 'every day',
    weekly: 'every week',
    biweekly: 'every 2 weeks',
    monthly: 'every month',
    quarterly: 'every quarter',
  })[f];

// dayList — approved-mockup.html:351-359
// 1 -> "Mondays"; 2 -> "Mondays & Tuesdays"; 3+ -> "A, B & C"; all 7 -> "any day"
export function dayList(days: WeekDay[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 0) return '—';
  if (sorted.length === 7) return 'any day';
  const names = sorted.map(d => DOW_FULL[d]);
  if (names.length === 1) return names[0] + 's';
  if (names.length === 2) return names[0] + 's & ' + names[1] + 's';
  return (
    names
      .slice(0, -1)
      .map(n => n + 's')
      .join(', ') +
    ' & ' +
    names[names.length - 1] +
    's'
  );
}

// firstDay — approved-mockup.html:360-363 (earliest selected weekday full name)
export function firstDay(days: WeekDay[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  return sorted.length ? DOW_FULL[sorted[0]] : '—';
}

// timeWindow — approved-mockup.html:364 (en-dash U+2013 + " UTC")
export function timeWindow(start: string, end: string): string {
  return start + '–' + end + ' UTC';
}

/**
 * Renders a day-of-month value as an English ordinal: 1 -> "1st", 2 -> "2nd",
 * 3 -> "3rd", 4 -> "4th", 11/12/13 -> "11th"/"12th"/"13th", 21 -> "21st", etc.
 *
 * `31` is special-cased to the literal phrase "last day" (no ordinal): it is
 * the "Last day" sentinel the picker writes, and the backend clamps 31 to each
 * month's true last day, so the read-back must say "last day", not "31st".
 *
 * Pure — no React, trivially unit-testable.
 */
export function ordinal(n: number): string {
  if (n === 31) return 'last day';
  const mod100 = n % 100;
  const mod10 = n % 10;
  let suffix = 'th';
  if (mod100 < 11 || mod100 > 13) {
    if (mod10 === 1) suffix = 'st';
    else if (mod10 === 2) suffix = 'nd';
    else if (mod10 === 3) suffix = 'rd';
  }
  return `${n}${suffix}`;
}

/** Clamps a persisted dayOfMonth to the UI's valid 1-31 range. The backend
 *  accepts unvalidated writes (0, 45, missing) and only normalizes at resolve
 *  time (0 => 1, over-long => month's last day); mirror that here so a raw
 *  persisted value can never leave the grid with no checked/focusable cell
 *  or render "0th" in the sentence. */
export function clampDayOfMonth(n: number | undefined): number {
  if (n === undefined || !Number.isFinite(n)) return 1;
  return Math.min(Math.max(Math.trunc(n), 1), 31);
}

/**
 * Weekly/biweekly cadence anchor day (0=Sun..6=Sat): the start of the LONGEST
 * contiguous block of selected days on the circular week — Friday for Fri–Sun,
 * Saturday for Sat–Mon. Ties in length break to the earliest (smallest) start.
 *
 * Why not just the smallest selected day: the backend resets the once-per-week
 * period on this day, and it must be one of the window days (validator). Picking
 * the numeric-min splits a week-wrapping window (e.g. Fri–Sun) across two periods
 * — the target scans Friday AND again Sunday. Anchoring on the block start keeps
 * the whole window inside one period. Empty or full week => 0 (Sunday).
 */
export function anchorDayOfWeek(days: number[]): number {
  const set = new Set(days);
  if (set.size === 0 || set.size === 7) return 0;

  let bestStart = 0;
  let bestLen = -1;
  for (let d = 0; d < 7; d++) {
    // A block starts at d only if d is selected and its predecessor is not.
    if (!set.has(d) || set.has((d + 6) % 7)) continue;
    let len = 0;
    while (set.has((d + len) % 7)) len++;
    // Strict >: iterating d ascending keeps the earliest start on a length tie.
    if (len > bestLen) {
      bestLen = len;
      bestStart = d;
    }
  }
  return bestStart;
}

/**
 * The four summary-sentence templates (FIDELITY §E, mockup `sentenceHTML`
 * lines 377-387). Returns ordered styled runs — NOT an HTML string — so the
 * renderer can apply chariot tokens and React stays XSS-safe.
 *
 * - E0 daily:     When all 7 days are selected, `dayList` is "any day" so the
 *                 day clause is omitted: Scan every day, between {win}. For a
 *                 subset, lead with "daily" (re-scan eligible on each selected
 *                 weekday): Scan daily, eligible on {days}, between {win}.
 * - E1 weekly:    Scan every week, on {days}, between {start}–{end} UTC.
 * - E2 biweekly:  Scan every 2 weeks, on the {days} window, between {win}.
 * - E3 monthly:   Scan every month, on the first {firstDay} window, between {win}.
 * - E4 quarterly: Scan every quarter (Jan · Apr · Jul · Oct), on the first
 *                 {firstDay} window, between {win}.
 */
export function buildSentence(s: ScanScheduleSummaryState): SentenceBodyPart[] {
  const win: SentenceBodyPart = {
    text: timeWindow(s.start, s.end),
    mono: true,
  };
  // Trailing clause: "… all day." for an all-day window, else "… between {win}.".
  const tail: SentenceBodyPart[] = s.allDay
    ? [{ text: ', all day.' }]
    : [{ text: ', between ' }, win, { text: '.' }];

  if (s.frequency === 'daily') {
    if (dayList(s.days) === 'any day') {
      return [{ text: 'Scan ' }, { text: 'every day', accent: true }, ...tail];
    }
    // Subset of weekdays: "every day" would contradict the restricting day
    // list, so lead with "daily" (eligibility) — re-scan is eligible on EACH
    // selected weekday, distinct from weekly's once-per-week semantics.
    return [
      { text: 'Scan ' },
      { text: 'daily', accent: true },
      { text: ', eligible on ' },
      { text: dayList(s.days), bold: true },
      ...tail,
    ];
  }
  if (s.frequency === 'weekly') {
    return [
      { text: 'Scan ' },
      { text: 'every week', accent: true },
      { text: ', on ' },
      { text: dayList(s.days), bold: true },
      ...tail,
    ];
  }
  if (s.frequency === 'biweekly') {
    return [
      { text: 'Scan ' },
      { text: 'every 2 weeks', accent: true },
      { text: ', on the ' },
      { text: dayList(s.days), bold: true },
      { text: ' window' },
      ...tail,
    ];
  }
  // The day-of-month clause is woven in only when the caller supplies
  // `dayOfMonth`; otherwise the monthly/quarterly sentences read exactly as they
  // did before the picker existed. `ordinal` renders 31 as "the last day".
  const dayPhrase: SentenceBodyPart[] =
    s.dayOfMonth !== undefined
      ? [
          { text: ' from the ' },
          { text: ordinal(clampDayOfMonth(s.dayOfMonth)), bold: true },
        ]
      : [];
  if (s.frequency === 'monthly') {
    return [
      { text: 'Scan ' },
      { text: 'every month', accent: true },
      ...dayPhrase,
      { text: ', on the ' },
      { text: 'first ' + firstDay(s.days), bold: true },
      { text: ' window' },
      ...tail,
    ];
  }
  // quarterly
  return [
    { text: 'Scan ' },
    { text: 'every quarter', accent: true },
    { text: ' (Jan · Apr · Jul · Oct)' },
    ...dayPhrase,
    { text: ', on the ' },
    { text: 'first ' + firstDay(s.days), bold: true },
    { text: ' window' },
    ...tail,
  ];
}

/**
 * Cadence-only ("Any time") summary: no launch window, so the sentence states
 * just the recurrence (and the day-of-month anchor for monthly/quarterly).
 * Used by the card and the wizard band when the schedule has no window.
 */
export function buildAnyTimeSentence(
  frequency: CadenceFrequency,
  dayOfMonth?: number
): SentenceBodyPart[] {
  const parts: SentenceBodyPart[] = [
    { text: 'Scan ' },
    { text: freqLead(frequency), accent: true },
  ];
  if (frequency === 'quarterly') {
    parts.push({ text: ' (Jan · Apr · Jul · Oct)' });
  }
  if (
    (frequency === 'monthly' || frequency === 'quarterly') &&
    dayOfMonth !== undefined
  ) {
    parts.push(
      { text: ' from the ' },
      { text: ordinal(clampDayOfMonth(dayOfMonth)), bold: true }
    );
  }
  parts.push({ text: ', any time.' });
  return parts;
}

/**
 * E5 pending state (no day selected in the wizard yet): the band shows only the
 * frequency phrase `Scan {freqLead}` — no trailing em-dash and no instruction.
 * `pending` is empty so the band can skip rendering the italic trailing run.
 */
export interface PendingSentence {
  lead: SentenceBodyPart[]; // "Scan {freqLead}"
  pending: string; // empty (no instruction copy)
}

export function buildPending(f: CadenceFrequency): PendingSentence {
  return {
    lead: [{ text: 'Scan ' }, { text: freqLead(f), accent: true }],
    pending: '',
  };
}

/**
 * A7 unset state (no `schedule` setting, or one without a nested cadence):
 * `No schedule set — Default: scan daily at 00:00 UTC (all days, all hours).`
 */
export const DEFAULT_SENTENCE: SentenceBodyPart[] = [
  { text: 'No schedule set — ' },
  { text: 'Default', bold: true },
  { text: ': scan ' },
  { text: 'daily', accent: true },
  { text: ' at ' },
  { text: '00:00 UTC', mono: true },
  { text: ' (all days, all hours).' },
];
